import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Calendar, Tag, Search, X } from "lucide-react";
import { api } from "../lib/api";
import { FourStars, StarsDivider } from "../components/FourStars";

export default function NewsListPage() {
  const [data, setData] = useState({ items: [], categories: [] });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");

  const load = useMemo(() => async () => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (cat) params.append("category", cat);
    const r = await api.get(`/news/search?${params.toString()}`).catch(() => ({ data: { items: [], categories: [] } }));
    setData(r.data);
  }, [q, cat]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="news-list-page">
      <section className="relative py-24 md:py-32 text-white overflow-hidden bg-gradient-to-br from-[#0A0F1A] via-[#0052CC] to-[#0A0F1A]">
        <div className="absolute inset-0 stars-pattern opacity-15" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <FourStars size={14} className="justify-center mb-4" />
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Actualités du mouvement</div>
          <h1 className="font-display text-4xl md:text-6xl mt-3 leading-tight">L'actualité des A.F.B.</h1>
          <p className="text-white/80 mt-6 max-w-2xl mx-auto leading-relaxed">
            Audiences, mobilisations, actions de terrain, réunions diplomatiques : suivez le mouvement au quotidien.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* Search bar */}
          <div className="flex flex-col md:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#475569]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher une actualité (titre, résumé, contenu)…"
                data-testid="news-search-input"
                className="w-full pl-11 pr-10 py-3 rounded-lg border border-black/10 bg-white text-sm focus:outline-none focus:border-[#D4AF37]"
              />
              {q && (
                <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#CE1126]">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Category chips */}
          {data.categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8" data-testid="news-categories">
              <button
                onClick={() => setCat("")}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                  !cat ? "bg-[#D4AF37] text-[#0A0F1A] border-[#D4AF37] font-semibold" : "bg-white border-black/10 text-[#475569] hover:border-[#D4AF37]/40"
                }`}
              >
                Toutes ({data.items.length})
              </button>
              {data.categories.map((c) => (
                <button
                  key={c.name}
                  onClick={() => setCat(c.name)}
                  data-testid={`news-cat-${c.name}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    cat === c.name ? "bg-[#D4AF37] text-[#0A0F1A] border-[#D4AF37] font-semibold" : "bg-white border-black/10 text-[#475569] hover:border-[#D4AF37]/40"
                  }`}
                >
                  {c.name} ({c.count})
                </button>
              ))}
            </div>
          )}

          {data.items.length === 0 ? (
            <div className="text-center py-20 text-[#475569] text-sm">
              Aucune actualité ne correspond à votre recherche.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.items.map((n, i) => (
                <Link
                  key={n.id}
                  to={`/actualites/${n.id}`}
                  data-testid={`news-list-card-${i}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={n.image_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                      <Tag size={11} /> {n.category}
                    </div>
                    <h3 className="font-display text-xl text-[#0A0F1A] mt-2 leading-tight line-clamp-2 group-hover:text-[#0052CC] transition-colors">{n.title}</h3>
                    <p className="text-sm text-[#475569] mt-2.5 leading-relaxed line-clamp-3">{n.summary}</p>
                    <div className="text-[11px] text-[#475569] mt-4 flex items-center gap-1.5">
                      <Calendar size={11} /> {new Date(n.published_at).toLocaleDateString("fr-FR", { dateStyle: "long" })}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      <StarsDivider />
    </div>
  );
}
